import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useDeviceProfile } from "../hooks/useDeviceProfile";
import { getWorkspace } from "../lib/workspaceStore";
import { saveProductHandoff } from "../lib/productHandoff";

const productSignals = [
  {
    name: "Portable blender cup",
    category: "Health & kitchen",
    status: "Trending",
    score: 94,
    margin: "58%",
    demand: "High TikTok velocity",
    source: "AliExpress, CJdropshipping",
    angle: "Fresh smoothies at work, gym, or campus.",
    colors: ["#5eead4", "#f97316"],
    image: "/assets/dropshipping/portable-blender-cup.jpg",
    proof: "Creator demos, impulse price, easy before/after.",
  },
  {
    name: "Magnetic phone cooler",
    category: "Mobile accessories",
    status: "Upcoming",
    score: 88,
    margin: "52%",
    demand: "Gaming + creator demand rising",
    source: "Alibaba, Temu",
    angle: "Stop overheating during streaming and mobile gaming.",
    colors: ["#60a5fa", "#111827"],
    image: "/assets/dropshipping/magnetic-phone-cooler.jpg",
    proof: "Clear problem, tech visual, strong comparison hook.",
  },
  {
    name: "Pet hair detail brush",
    category: "Home & auto",
    status: "Top selling",
    score: 91,
    margin: "64%",
    demand: "Consistent evergreen buyer intent",
    source: "Zendrop, Spocket",
    angle: "Clean couches, carpets, and car seats in seconds.",
    colors: ["#fbbf24", "#7c3aed"],
    image: "/assets/dropshipping/pet-hair-detail-brush.jpg",
    proof: "Satisfying cleanup content and repeatable household pain.",
  },
  {
    name: "LED lash mirror",
    category: "Beauty",
    status: "New",
    score: 83,
    margin: "61%",
    demand: "Beauty creator demos perform well",
    source: "Alibaba, DSers",
    angle: "Salon-level detail lighting from a travel mirror.",
    colors: ["#fb7185", "#8b5cf6"],
    image: "/assets/dropshipping/led-lash-mirror.jpg",
    proof: "Visual transformation, gifting angle, low shipping weight.",
  },
  {
    name: "Cordless neck massager",
    category: "Wellness",
    status: "Scaling",
    score: 90,
    margin: "55%",
    demand: "Strong pain-relief search intent",
    source: "CJdropshipping, Amazon",
    angle: "Relax tight shoulders after work without booking a massage.",
    colors: ["#22c55e", "#0f172a"],
    image: "/assets/dropshipping/cordless-neck-massager.jpg",
    proof: "Pain-point product with demo-friendly relief messaging.",
  },
  {
    name: "Mini humidifier lamp",
    category: "Home decor",
    status: "Seasonal",
    score: 86,
    margin: "49%",
    demand: "Giftable desk and bedroom trend",
    source: "Temu, AliExpress",
    angle: "Make any room feel calmer with mist, light, and scent.",
    colors: ["#38bdf8", "#facc15"],
    image: "/assets/dropshipping/mini-humidifier-lamp.jpg",
    proof: "Aesthetic video, gift angle, cozy room content.",
  },
];

const themeTemplates = [
  {
    name: "Viral One Product",
    speed: "10 min launch",
    bestFor: "TikTok-style product tests",
    sections: ["Announcement bar", "Hero video", "Benefits", "Reviews", "FAQ", "Sticky checkout"],
    palette: ["#0f172a", "#14b8a6", "#f8fafc"],
    image: "/assets/dropshipping/portable-blender-cup.jpg",
  },
  {
    name: "Beauty Glow",
    speed: "15 min launch",
    bestFor: "Beauty, wellness, and UGC offers",
    sections: ["Before/after", "Creator proof", "Bundle deal", "Testimonials", "Guarantee"],
    palette: ["#831843", "#fb7185", "#fff1f2"],
    image: "/assets/dropshipping/led-lash-mirror.jpg",
  },
  {
    name: "Gadget Lab",
    speed: "12 min launch",
    bestFor: "Tech, phone, gaming, and desk gadgets",
    sections: ["Feature grid", "Comparison table", "Spec strip", "Demo carousel", "Urgency ticker"],
    palette: ["#111827", "#60a5fa", "#dbeafe"],
    image: "/assets/dropshipping/magnetic-phone-cooler.jpg",
  },
  {
    name: "Pet Proof",
    speed: "12 min launch",
    bestFor: "Pet, home, auto, and cleaning products",
    sections: ["Problem/solution", "Proof images", "Social comments", "Bundle pricing", "Track order"],
    palette: ["#3f2a1d", "#f59e0b", "#fff7ed"],
    image: "/assets/dropshipping/pet-hair-detail-brush.jpg",
  },
  {
    name: "Premium Bundle",
    speed: "18 min launch",
    bestFor: "Higher-ticket bundles and upsells",
    sections: ["Logo list", "Bundle deals", "Comparison table", "Guarantee", "Email capture"],
    palette: ["#172554", "#f97316", "#eff6ff"],
    image: "/assets/dropshipping/cordless-neck-massager.jpg",
  },
  {
    name: "Fast Trust Store",
    speed: "8 min launch",
    bestFor: "Quick validation pages before full store build",
    sections: ["Trust badges", "Icon bar", "Reviews", "Shipping promise", "FAQ"],
    palette: ["#064e3b", "#a7f3d0", "#ecfdf5"],
    image: "/assets/dropshipping/mini-humidifier-lamp.jpg",
  },
];

const masterclassTools = [
  { name: "Product Miner", desc: "Import product-feed records and identify the evidence still needed to test each product.", action: "List imported products, identify missing demand, cost, shipping, and competition sources, then create a research plan without calling any product a winner." },
  { name: "Offer Builder", desc: "Create bundles, discounts, guarantees, urgency, and profit math.", action: "Build a launch offer with bundle pricing, urgency, guarantee, margin math, and upsell ideas." },
  { name: "Creative Lab", desc: "Generate hooks, UGC scripts, avatar videos, and split-test briefs.", action: "Create 10 hooks, 5 UGC scripts, 3 avatar video angles, and a split-test plan." },
  { name: "Ad Research Board", desc: "Turn approved Meta, TikTok, YouTube, and creator sources into a documented creative brief.", action: "Compare only verified provider data, identify creative hypotheses, and flag which signals still need validation." },
  { name: "Landing Page Coach", desc: "Score hero, proof, CTA, shipping promise, reviews, and FAQ quality.", action: "Score the dropshipping page and rewrite weak hero, proof, CTA, shipping, reviews, and FAQ sections." },
  { name: "Supplier Auditor", desc: "Check shipping speed, stock risk, variants, cost, and return issues.", action: "Audit supplier risk, variant quality, shipping time, cost, stock issues, and refund concerns." },
  { name: "Launch Calendar", desc: "Plan 48-hour, 7-day, and 30-day testing cycles with notifications.", action: "Create a 48-hour, 7-day, and 30-day test calendar with notification triggers." },
  { name: "Scale Room", desc: "Build a controlled scale plan once verified campaign and store data are connected.", action: "Prepare pause, refresh, retarget, and budget recommendations; require verified live results and customer approval before any action." },
];

const priorityConnectors = [
  {
    name: "Shopify",
    stage: "First connector",
    status: "Public feeds ready",
    color: "#95bf47",
    summary: "Pull products, prices, images, vendors, inventory, collections, and Shopify catalog feeds.",
    capabilities: ["Products", "Prices", "Images", "Vendors", "Inventory", "Collections"],
    next: "Paste a public /products.json feed now. Private catalog, inventory, and order access require a securely configured Shopify Admin API connection.",
  },
  {
    name: "CJdropshipping",
    stage: "Supplier sourcing",
    status: "Provider setup required",
    color: "#f97316",
    summary: "Bring in supplier products, variants, item cost, shipping options, stock risk, and fulfillment data.",
    capabilities: ["Supplier products", "Variants", "Cost", "Shipping", "Stock risk", "Fulfillment"],
    next: "CJ supplier data is unavailable until a server-side provider connection is configured. OmniMark will not estimate stock or shipping in the meantime.",
  },
  {
    name: "Meta Marketing API",
    stage: "Ad performance",
    status: "Credentials needed",
    color: "#2563eb",
    summary: "Read campaign, ad set, ad creative, spend, reach, CTR, CPA, ROAS, and winning-video signals.",
    capabilities: ["Campaigns", "Creatives", "Spend", "CTR", "CPA", "ROAS"],
    next: "Meta metrics are unavailable until an approved app, secure token exchange, and server-side worker are configured.",
  },
  {
    name: "TikTok Business API",
    stage: "Creative signals",
    status: "Credentials needed",
    color: "#111827",
    summary: "Pull ad and creative signals for product discovery, UGC testing, hooks, audience response, and trends.",
    capabilities: ["Ads", "Creative signals", "UGC tests", "Hooks", "Trends", "Product discovery"],
    next: "TikTok metrics are unavailable until an approved app, secure token exchange, and server-side worker are configured.",
  },
  {
    name: "Google Ads Keyword Planner",
    stage: "Demand validation",
    status: "Credentials required",
    color: "#4285f4",
    summary: "Validate product language with keyword ideas and historical search metrics using an authorized Google Ads account.",
    capabilities: ["Keyword ideas", "Search volume", "Competition", "Location", "Language"],
    next: "Requires a Google Ads developer token, OAuth, and customer account before demand scores can be shown.",
  },
  {
    name: "YouTube creator research",
    stage: "Creator discovery",
    status: "API key required",
    color: "#ff0033",
    summary: "Research public video and channel search results for product angles and outreach candidates; it does not publish, message, or pay creators.",
    capabilities: ["Video search", "Channel search", "Thumbnails", "Public metadata", "Research brief"],
    next: "Requires a configured YouTube Data API project and key. Creator contact, publishing, and payouts remain separate approved workflows.",
  },
];

const liveGrowthMetrics = [
  { label: "Sales by product", value: "342", detail: "Portable blender cup leads with 128 orders" },
  { label: "Revenue", value: "$18,420", detail: "$7,185 est. profit • 39% margin" },
  { label: "Refunds", value: "2.1%", detail: "Below alert threshold" },
  { label: "Conversion rate", value: "3.8%", detail: "$41.60 average order value" },
  { label: "Ad spend", value: "$3,240", detail: "ROAS 3.9x • CPA $9.47" },
  { label: "Traffic quality", value: "1.6% CTR", detail: "$12.80 CPM • Meta winning" },
  { label: "Inventory risk", value: "Medium", detail: "CJ stock 11 days remaining" },
  { label: "Shipping health", value: "92%", detail: "8 delayed orders need follow-up" },
];

const liveProducts = [
  { name: "Portable blender cup", revenue: "$7,680", profit: "$3,104", units: 128, conversion: "4.6%", roas: "4.8x", status: "Scale", risk: "Low" },
  { name: "Pet hair detail brush", revenue: "$4,950", profit: "$2,079", units: 99, conversion: "3.9%", roas: "3.6x", status: "Test bundle", risk: "Low" },
  { name: "LED lash mirror", revenue: "$3,140", profit: "$1,099", units: 62, conversion: "2.7%", roas: "2.4x", status: "Refresh video", risk: "Medium" },
  { name: "Magnetic phone cooler", revenue: "$2,650", profit: "$903", units: 53, conversion: "2.3%", roas: "1.8x", status: "Pause weak ad", risk: "High" },
];

const creativeSignals = [
  { name: "Hook A - gym smoothie demo", result: "Winning", metric: "4.8x ROAS", action: "Duplicate winner" },
  { name: "Hook B - desk cleanup before/after", result: "Winning", metric: "3.6x ROAS", action: "Increase budget 15%" },
  { name: "Hook C - beauty mirror unboxing", result: "Weak", metric: "1.4x ROAS", action: "Create new video" },
  { name: "Hook D - phone cooler gaming test", result: "Weak", metric: "0.9x ROAS", action: "Pause ad" },
];

const aiGrowthRecommendations = [
  { title: "Raise blender price by $2", detail: "Margin can improve without hurting conversion based on current AOV and demand.", impact: "+$256 weekly profit" },
  { title: "Duplicate winning smoothie video", detail: "Best creative has strong watch-through and lowest CPA.", impact: "More volume" },
  { title: "Test pet brush bundle", detail: "High repeat pain point. Offer 2-pack and car/home bundle.", impact: "+18% AOV target" },
  { title: "Pause phone cooler ad set", detail: "CPA is above margin room and supplier stock risk is high.", impact: "Protect margin" },
  { title: "Create new lash mirror UGC", detail: "Current creative is weak, but product margin and demo value are still useful.", impact: "Recover ROAS" },
  { title: "Send delayed-order notification", detail: "8 customers have delayed tracking. Proactive update can reduce complaints.", impact: "Lower refunds" },
];

const researchBuckets = [
  "New product radar",
  "Trending products",
  "Doing well selling",
  "Upcoming winners",
  "Top selling products",
  "High-margin products",
  "Low-competition tests",
  "Fast-shipping suppliers",
  "Creator video winners",
  "Competitor page teardowns",
  "Bundle opportunities",
  "Seasonal calendars",
];

const bucketActions = {
  "New product radar": { product: "LED lash mirror", theme: "Fast Trust Store", tool: "Product Miner", note: "surface fresh products with early demand before the market is crowded" },
  "Trending products": { product: "Portable blender cup", theme: "Viral One Product", tool: "Product Miner", note: "prioritize high-velocity products with active social proof" },
  "Doing well selling": { product: "Pet hair detail brush", theme: "Pet Proof", tool: "Offer Builder", note: "find products with proven purchase intent and repeatable offers" },
  "Upcoming winners": { product: "Magnetic phone cooler", theme: "Gadget Lab", tool: "Ad Spy Board", note: "spot products gaining momentum before they peak" },
  "Top selling products": { product: "Pet hair detail brush", theme: "Premium Bundle", tool: "Scale Room", note: "rank proven sellers by margin, supplier strength, and scaling potential" },
  "High-margin products": { product: "LED lash mirror", theme: "Beauty Glow", tool: "Offer Builder", note: "focus on products with room for bundles, upsells, and profitable ad spend" },
  "Low-competition tests": { product: "Mini humidifier lamp", theme: "Fast Trust Store", tool: "Landing Page Coach", note: "find underserved products with clean positioning and simple proof" },
  "Fast-shipping suppliers": { product: "Cordless neck massager", theme: "Premium Bundle", tool: "Supplier Auditor", note: "prioritize suppliers with better shipping speed, stock reliability, and lower refund risk" },
  "Creator video winners": { product: "Magnetic phone cooler", theme: "Gadget Lab", tool: "Creative Lab", note: "choose products that can win with UGC hooks, demos, and short-form video" },
  "Competitor page teardowns": { product: "Portable blender cup", theme: "Viral One Product", tool: "Landing Page Coach", note: "compare competitor pages and produce a stronger test page" },
  "Bundle opportunities": { product: "Cordless neck massager", theme: "Premium Bundle", tool: "Offer Builder", note: "identify products that work as bundles, upsells, and multi-pack offers" },
  "Seasonal calendars": { product: "Mini humidifier lamp", theme: "Beauty Glow", tool: "Launch Calendar", note: "map products to seasonal launches, gift windows, and reminder notifications" },
};

function accessForPlan(usage) {
  const plan = usage?.effective_plan || usage?.plan || "trial";
  if (usage?.is_admin) {
    return { label: "Admin unlimited", plan: "admin", scans: "Unlimited", themes: "All", locked: false, limitCopy: "Unlimited product scans, all themes, all AI masterclass tools." };
  }
  if (plan === "agency") {
    return { label: "Agency command room", plan, scans: "25,000/day", themes: "All", locked: false, limitCopy: "High-volume scans, all themes, team workflows, and campaign alerts." };
  }
  if (plan === "pro") {
    return { label: "Pro scanner", plan, scans: "1,000/day", themes: "6", locked: false, limitCopy: "Scan up to 1,000 products per day with AI scoring and fast test pages." };
  }
  if (plan === "starter") {
    return { label: "Starter testing", plan, scans: "100/day", themes: "3", locked: false, limitCopy: "Limited product scans and starter test themes for early validation." };
  }
  return { label: "Upgrade required", plan, scans: "Locked", themes: "Locked", locked: true, limitCopy: "Upgrade to scan products and launch test pages." };
}

export default function DropshippingPage() {
  const navigate = useNavigate();
  const workspace = getWorkspace();
  const device = useDeviceProfile();
  const [usage, setUsage] = useState(null);
  const [productUrl, setProductUrl] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(productSignals[0]);
  const [selectedTheme, setSelectedTheme] = useState(themeTemplates[0]);
  const [selectedTool, setSelectedTool] = useState(masterclassTools[0]);
  const [selectedBucket, setSelectedBucket] = useState(researchBuckets[0]);
  const [scanCount, setScanCount] = useState(1000);
  const [scanResult, setScanResult] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notificationsOptIn, setNotificationsOptIn] = useState(false);
  const [managedGrowth, setManagedGrowth] = useState(false);
  const [growthActions, setGrowthActions] = useState([
    "Planning mode: connect verified Shopify, supplier, Meta, or TikTok data before OmniMark can show live performance or take live actions.",
  ]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/users/me");
        setUsage(res.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load subscription access.");
      }
    })();
  }, []);

  const access = accessForPlan(usage);
  const maxScan = access.plan === "agency" || access.plan === "admin" ? 25000 : access.plan === "pro" ? 1000 : access.plan === "starter" ? 100 : 0;
  const effectiveScan = Math.min(scanCount, maxScan || scanCount);
  const compact = device.isMobile || device.width < 860;

  const prompt = useMemo(() => {
    const url = productUrl.trim() || "[paste product or supplier URL here]";
    const wsName = workspace?.name || "this workspace";
    const bucketNote = bucketActions[selectedBucket]?.note || "find winning dropshipping products";
    return `OmniMark HQ Dropshipping AI: analyze ${url} for ${wsName}. Research mode: ${selectedBucket} - ${bucketNote}. Use the ${selectedTheme.name} pre-built testing theme. Run the ${selectedTool.name} workflow: ${selectedTool.action} Import up to ${effectiveScan} source records if available. Use only verified source fields; do not claim demand, margin, supplier risk, shipping, ad saturation, or creator performance without its corresponding approved data source. For every imported product, list the missing evidence and create a research plan, page brief, offer hypotheses, hooks, 5 video-script concepts, and a 48-hour test plan that requires customer approval before launch. Product angle to start with: ${selectedProduct.angle}`;
  }, [productUrl, selectedTheme, selectedTool, selectedBucket, effectiveScan, selectedProduct, workspace?.name]);

  function selectResearchBucket(bucket) {
    const next = bucketActions[bucket];
    setSelectedBucket(bucket);
    if (!next) return;
    const product = productSignals.find((item) => item.name === next.product);
    const theme = themeTemplates.find((item) => item.name === next.theme);
    const tool = masterclassTools.find((item) => item.name === next.tool);
    if (product) setSelectedProduct(product);
    if (theme) setSelectedTheme(theme);
    if (tool) setSelectedTool(tool);
    setCopied(false);
  }

  async function copyPrompt() {
    setCopied(false);
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch {
      setErr("Could not copy. Select the text and copy it manually.");
    }
  }

  async function runScanner() {
    setErr(null);
    setScanBusy(true);
    setScanResult(null);
    try {
      if (!workspace?.id) {
        setErr("Select a workspace before running a product scan.");
        return;
      }
      if (!/^https?:\/\//i.test(productUrl.trim())) {
        setErr("Paste a public product page, Shopify /products.json, supplier JSON, or CSV URL before scanning. OmniMark will not fabricate live results.");
        return;
      }
      const res = await api.post(`/api/workspaces/${workspace.id}/dropshipping/scan`, {
        requested_count: effectiveScan || scanCount,
        product_url: productUrl.trim() || null,
        category: selectedProduct.category,
      });
      setScanResult(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Product scan failed. Check connections and subscription access.");
    } finally {
      setScanBusy(false);
    }
  }

  function handoffProduct(destination, item = selectedProduct) {
    const handoff = {
      product: item?.name || "",
      category: item?.category || selectedProduct.category,
      angle: item?.angle || selectedProduct.angle,
      supplier: item?.supplier || "",
      theme: selectedTheme.name,
      workflow: selectedTool.name,
      source_url: productUrl.trim(),
      brief: prompt,
      created_at: new Date().toISOString(),
    };
    try {
      saveProductHandoff(handoff, workspace?.id);
    } catch (error) {
      setErr(error.message || "Could not prepare the product handoff.");
      return;
    }
    navigate(destination);
  }

  function recordGrowthAction(action) {
    setGrowthActions((items) => {
      const next = `${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${action}`;
      return [next, ...items].slice(0, 6);
    });
  }

  return (
    <div style={page} data-testid="dropshipping-page">
      <section style={compact ? heroCompact : hero}>
        <div style={heroCopy}>
          <div style={eyebrow}>AI dropshipping lab + product scanner</div>
          <h1 style={title}>Find winners. Test pages fast. Scale what proves itself.</h1>
          <p style={subtitle}>
            Import a public product feed, build a test page, create ad angles, and use AI to turn verified product data into research briefs, offers, creatives, and scaling plans.
          </p>
          <div style={heroActions}>
            <button style={button} onClick={copyPrompt} disabled={access.locked}>{copied ? "AI brief copied" : "Copy AI test brief"}</button>
            <button style={brightButton} onClick={runScanner} disabled={access.locked || scanBusy}>{scanBusy ? "Scanning..." : "Run scanner"}</button>
            <Link to="/app/connections" style={lightButton}>Connect suppliers</Link>
          </div>
        </div>
        <div style={compact ? heroVisualCompact : heroVisual}>
          {productSignals.slice(0, 4).map((product, i) => (
            <div key={product.name} style={{ ...floatingProduct, transform: `translateY(${i % 2 ? 14 : -4}px)` }}>
              <img src={product.image} alt={product.name} style={productArt} />
              <div>
                <strong>{product.name}</strong>
                <div style={tinyText}>Planning test idea • source data required</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {err && <div style={errorBox}>{err}</div>}

      <section style={limitGrid}>
        <div style={access.locked ? lockedBadge : badge}>{access.label}</div>
        <Metric label="Product scans" value={access.scans} />
        <Metric label="Theme access" value={access.themes} />
        <Metric label="AI tools" value={access.locked ? "Locked" : "Included"} />
      </section>

      <section style={upgradeBand}>
        <div>
          <strong>{access.limitCopy}</strong>
          <div style={muted}>Pro is built for 1,000-product scans. Agency gets high-volume product discovery and full dropshipping command-room access.</div>
        </div>
        {access.locked && <Link to="/app/upgrade" style={blackButton}>Upgrade</Link>}
      </section>

      <section style={panel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Verified Research Sources</div>
            <div style={muted}>These sources cover catalog, supplier, demand, ad, and creator research. A source only becomes live after its server-side credentials and provider approval are configured.</div>
          </div>
          <Link to="/app/connections" style={outlineButton}>Manage connections</Link>
        </div>
        <div style={connectorGrid}>
          {priorityConnectors.map((connector) => (
            <div key={connector.name} style={connectorCard}>
              <div style={connectorTop}>
                <div style={{ ...connectorMark, background: connector.color }}>{connector.name.slice(0, 1)}</div>
                <div>
                  <strong>{connector.name}</strong>
                  <div style={muted}>{connector.stage}</div>
                </div>
                <span style={statusPill}>{connector.status}</span>
              </div>
              <div style={connectorSummary}>{connector.summary}</div>
              <div style={keywordWrap}>
                {connector.capabilities.map((capability) => <span key={capability} style={statusPill}>{capability}</span>)}
              </div>
              <div style={hintBox}>{connector.next}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={compact ? gridCompact : grid}>
        <div style={panel}>
          <div style={sectionHeader}>
            <div>
            <div style={sectionTitle}>Source-backed Product Scanner</div>
              <div style={muted}>Import a public Shopify /products.json catalog or supplier JSON/CSV feed. A normal product page is read only for research context; it cannot become a made-up winner list.</div>
            </div>
            <div style={scannerBadge}>{access.scans}</div>
          </div>

          <div style={scannerControl}>
            <label style={label}>Products to scan</label>
            <input
              type="range"
              min={access.plan === "starter" ? 25 : 100}
              max={maxScan || 1000}
              step={access.plan === "agency" || access.plan === "admin" ? 500 : 25}
              value={effectiveScan || 0}
              onChange={(e) => setScanCount(Number(e.target.value))}
              disabled={access.locked}
              style={{ width: "100%" }}
            />
            <div style={scanNumber}>{access.locked ? "Locked" : `${effectiveScan.toLocaleString()} products`}</div>
          </div>

          <div style={scanActions}>
            <button onClick={runScanner} disabled={access.locked || scanBusy} style={button}>
              {scanBusy ? "Scanning products..." : `Scan ${effectiveScan.toLocaleString()} products`}
            </button>
            <div style={muted}>Shows up to 60 imported source records. Ranking stays unavailable until verified demand, cost, shipping, and competition sources are connected.</div>
          </div>

          <div style={bucketGrid}>
            {researchBuckets.map((bucket) => (
              <button
                key={bucket}
                type="button"
                onClick={() => selectResearchBucket(bucket)}
                style={selectedBucket === bucket ? bucketButtonActive : bucketButton}
              >
                {bucket}
              </button>
            ))}
          </div>
          <div style={hintBox}>
            Active research mode: <strong>{selectedBucket}</strong>. The AI brief, product angle, theme, and masterclass workflow update when you choose a mode.
          </div>
        </div>

        <div style={panel}>
          <div style={sectionTitle}>AI Page Builder Brief</div>
          <label style={label}>Product or supplier URL</label>
          <input
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder="Paste product URL, Shopify /products.json, supplier JSON feed, or CSV feed"
            style={input}
            disabled={access.locked}
          />
          <div style={hintBox}>
            Available now: public Shopify catalogs and supplier JSON/CSV feeds. A normal product page returns keywords and page context only. Private store, ad, trend, and creator data require the provider access shown below.
          </div>
          <label style={label}>Pre-built testing theme</label>
          <select value={selectedTheme.name} onChange={(e) => setSelectedTheme(themeTemplates.find((t) => t.name === e.target.value) || themeTemplates[0])} style={input}>
            {themeTemplates.map((theme) => <option key={theme.name}>{theme.name}</option>)}
          </select>
          <label style={label}>Copy this to OmniMark AI</label>
          <textarea value={prompt} readOnly rows={10} style={textarea} />
          <div style={actions}>
            <button onClick={copyPrompt} disabled={access.locked} style={button}>{copied ? "Copied" : "Copy AI brief"}</button>
            <button type="button" onClick={() => handoffProduct("/app/website")} style={outlineButton}>Build page</button>
            <button type="button" onClick={() => handoffProduct("/app/studio")} style={outlineButton}>Create videos</button>
          </div>
        </div>
      </section>

      <section style={panel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Product Test-Idea Templates</div>
            <div style={muted}>These are planning examples for building a test brief—not current trend, supplier, margin, or sales results. Run a source-backed scan to see real imported records.</div>
          </div>
          <button onClick={runScanner} disabled={access.locked || scanBusy} style={outlineButton}>{scanBusy ? "Scanning..." : "Refresh winners"}</button>
        </div>
        <div style={productGridWide}>
          {productSignals.map((product) => (
            <button
              key={product.name}
              onClick={() => setSelectedProduct(product)}
              style={selectedProduct.name === product.name ? productCardActive : productCard}
            >
              <img src={product.image} alt={product.name} style={productArtSmall} />
              <span style={statusPill}>Planning example</span>
              <strong>{product.name}</strong>
              <span style={muted}>{product.category}</span>
              <span>Suggested angle: {product.angle}</span>
              <span style={muted}>{product.proof}</span>
            </button>
          ))}
        </div>
      </section>

      <section style={panel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Growth Planning Preview</div>
            <div style={muted}>Illustrative planning data only—not sales, revenue, inventory, or ad data from your business. Connect verified sources before OmniMark labels any metric live.</div>
          </div>
          <div style={growthHeaderActions}>
            <Link to="/app/connections" style={outlineButton}>Connect store data</Link>
          </div>
        </div>

        <div style={growthMetricGrid}>
          {liveGrowthMetrics.map((metricItem) => (
            <Metric key={metricItem.label} label={metricItem.label} value={metricItem.value} />
          ))}
        </div>
        <div style={growthDetailGrid}>
          {liveGrowthMetrics.map((metricItem) => (
            <div key={metricItem.label} style={tinyInsight}>
              <strong>{metricItem.label}</strong>
              <span>{metricItem.detail}</span>
            </div>
          ))}
        </div>

        <div style={compact ? growthSplitCompact : growthSplit}>
          <div style={growthPanel}>
            <div style={sectionTitle}>Example Product Planning Board</div>
            <div style={tableWrap}>
              <table style={dataTable}>
                <thead>
                  <tr>
                    <th style={tableHead}>Product</th>
                    <th style={tableHead}>Revenue</th>
                    <th style={tableHead}>Profit</th>
                    <th style={tableHead}>Units</th>
                    <th style={tableHead}>CVR</th>
                    <th style={tableHead}>ROAS</th>
                    <th style={tableHead}>AI move</th>
                  </tr>
                </thead>
                <tbody>
                  {liveProducts.map((product) => (
                    <tr key={product.name}>
                      <td style={tableCell}><strong>{product.name}</strong><div style={muted}>Risk: {product.risk}</div></td>
                      <td style={tableCell}>{product.revenue}</td>
                      <td style={tableCell}>{product.profit}</td>
                      <td style={tableCell}>{product.units}</td>
                      <td style={tableCell}>{product.conversion}</td>
                      <td style={tableCell}>{product.roas}</td>
                      <td style={tableCell}><span style={statusPill}>{product.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={growthPanel}>
            <div style={sectionTitle}>Example Creative Review</div>
            <div style={videoSignalList}>
              {creativeSignals.map((creative) => (
                <button
                  key={creative.name}
                  type="button"
                  onClick={() => recordGrowthAction(`${creative.action}: ${creative.name}`)}
                  style={creative.result === "Winning" ? videoSignalWinning : videoSignalWeak}
                >
                  <strong>{creative.name}</strong>
                  <span>{creative.result} • {creative.metric}</span>
                  <span style={statusPill}>{creative.action}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={compact ? growthSplitCompact : growthSplit}>
          <div style={growthPanel}>
            <div style={sectionTitle}>Example Planning Recommendations</div>
            <div style={recommendationGrid}>
              {aiGrowthRecommendations.map((rec) => (
                <div key={rec.title} style={recommendationCard}>
                  <strong>{rec.title}</strong>
                  <div style={muted}>{rec.detail}</div>
                  <div style={recFooter}>
                    <span style={statusPill}>{rec.impact}</span>
                    <button type="button" onClick={() => recordGrowthAction(`Saved planning idea: ${rec.title}`)} style={miniButton}>Save idea</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={managedPanel}>
            <div style={sectionTitle}>Managed Growth Readiness</div>
            <p style={managedCopy}>
              Performance-based management cannot be enabled from planning data. It needs verified store and ad sources, an approved customer agreement, and an auditable approval workflow before any AI recommendation can affect a live campaign.
            </p>
            <div style={feeGrid}>
              <div style={feeCard}>
                <strong>Revenue option</strong>
                <span>1% to 3% of OmniMark-attributed sales</span>
              </div>
              <div style={feeCard}>
                <strong>Profit-lift option</strong>
                <span>5% to 10% of incremental profit lift</span>
              </div>
            </div>
            <div style={hintBox}>Not enabled: OmniMark does not currently send live campaign changes, charge a performance fee, or trigger payouts from this planning screen.</div>
          </div>
        </div>

        <div style={activityLog}>
          <strong>Growth action log</strong>
          {growthActions.map((item) => <span key={item}>{item}</span>)}
        </div>
      </section>

      {scanResult && (
        <section style={panel}>
          <div style={sectionHeader}>
            <div>
              <div style={sectionTitle}>Scanner Results</div>
              <div style={muted}>
                Imported {scanResult.scanned_count?.toLocaleString()} source records. Showing {scanResult.returned_count || scanResult.items?.length || 0} records from that source; OmniMark does not rank a winner until demand, cost, shipping, and competition sources are verified.
              </div>
            </div>
            <div style={scannerBadge}>{scanResult.plan}</div>
          </div>

          {scanResult.message && <div style={errorBox}>{scanResult.message}</div>}

          {scanResult.summary && (
            <div style={limitGrid}>
              <Metric label="Imported records" value={scanResult.summary.imported_records ?? 0} />
              <Metric label="Listed prices" value={scanResult.summary.priced_records ?? 0} />
              <Metric label="Demand signals" value={scanResult.summary.demand_status || "Not verified"} />
              <Metric label="Supplier decision" value={scanResult.summary.best_supplier || "Not ranked"} />
            </div>
          )}

          <div style={resultNotice}>
            <strong>Source evidence mode:</strong> these are imported catalog records. OmniMark will only label a product a winner after verified demand, supplier cost/shipping, and competitive signals are available.
          </div>

          {scanResult.context?.url && (
            <div style={contextBox}>
              <strong>URL scan context</strong>
              <div style={muted}>
                {scanResult.context.page_title || scanResult.context.fetch_error || `HTTP ${scanResult.context.fetch_status || "checked"}`}
              </div>
              <div style={muted}>
                Source: {scanResult.context.source_mode || "page"}{scanResult.context.feed_count ? ` • ${scanResult.context.feed_count} feed products imported` : ""}
              </div>
              {!!scanResult.context.keywords?.length && (
                <div style={keywordWrap}>
                  {scanResult.context.keywords.slice(0, 10).map((kw) => <span key={kw} style={statusPill}>{kw}</span>)}
                </div>
              )}
            </div>
          )}

          <div style={resultGrid}>
            {(scanResult.items || []).map((item) => (
              <div key={item.id} style={resultCard}>
                <div style={resultTop}>
                  <strong>{item.name}</strong>
                  <span style={scorePill}>Imported</span>
                </div>
                <div style={muted}>{item.category} • {item.supplier}</div>
                <div style={resultMetrics}>
                  <span>Listed price {item.suggested_price == null ? "Not supplied" : `$${item.suggested_price}`}</span>
                  <span>Demand Not verified</span>
                  <span>Shipping Not verified</span>
                  <span>Risk {item.supplier_risk}</span>
                </div>
                <div style={themeSections}>{item.angle}</div>
                <div style={resultFooter}>
                  <span style={statusPill}>{item.status}</span>
                  <span style={muted}>{item.recommended_theme} • {item.next_action}</span>
                </div>
                <div style={resultActions}>
                  <button
                    type="button"
                    onClick={() => {
                      setProductUrl(item.name);
                      setSelectedBucket("Top selling products");
                      setSelectedProduct(productSignals.find((product) => product.category === item.category) || selectedProduct);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    style={miniButton}
                  >
                    Use product
                  </button>
                  <button type="button" onClick={() => handoffProduct("/app/website", item)} style={miniLink}>Build page</button>
                  <button type="button" onClick={() => handoffProduct("/app/studio", item)} style={miniLink}>Video brief</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={panel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Pre-built Dropshipping Themes</div>
            <div style={muted}>Inspired by your uploaded Shopify theme package: announcement bars, sliders, testimonials, icon bars, comparison tables, bundles, tracking, reviews, and trust sections.</div>
          </div>
        </div>
        <div style={themeGrid}>
          {themeTemplates.map((theme) => (
            <button key={theme.name} onClick={() => setSelectedTheme(theme)} style={selectedTheme.name === theme.name ? themeCardActive : themeCard}>
              <div style={themePreview}>
                <div style={{ ...themeHeroBar, background: theme.palette[0] }} />
                <img src={theme.image} alt={`${theme.name} preview`} style={themeImage} />
                <div style={themeLines}><span /><span /><span /></div>
              </div>
              <strong>{theme.name}</strong>
              <span style={muted}>{theme.bestFor}</span>
              <span style={statusPill}>{theme.speed}</span>
              <div style={themeSections}>{theme.sections.slice(0, 4).join(" • ")}</div>
            </button>
          ))}
        </div>
      </section>

      <section style={panel}>
        <div style={sectionTitle}>AI Ecom Masterclass Tools</div>
        <div style={masterGrid}>
          {masterclassTools.map((tool) => (
            <button
              key={tool.name}
              onClick={() => setSelectedTool(tool)}
              style={selectedTool.name === tool.name ? masterCardActive : masterCard}
            >
              <div style={toolIcon}>{tool.name.slice(0, 1)}</div>
              <div>
                <strong>{tool.name}</strong>
                <div style={muted}>{tool.desc}</div>
              </div>
            </button>
          ))}
        </div>
        <div style={toolConsole}>
          <div>
            <div style={sectionTitle}>{selectedTool.name} Workflow</div>
            <div style={muted}>{selectedTool.action}</div>
          </div>
          <textarea value={prompt} readOnly rows={5} style={textareaCompact} />
          <div style={actions}>
            <button onClick={copyPrompt} disabled={access.locked} style={button}>{copied ? "Copied" : "Copy workflow brief"}</button>
            <button onClick={runScanner} disabled={access.locked || scanBusy} style={outlineButton}>{scanBusy ? "Scanning..." : "Run with scanner"}</button>
            <button type="button" onClick={() => handoffProduct("/app/studio")} style={outlineButton}>Create videos</button>
            <button type="button" onClick={() => handoffProduct("/app/website")} style={outlineButton}>Build page</button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={metric}>
      <div style={metricValue}>{value}</div>
      <div style={muted}>{label}</div>
    </div>
  );
}

const page = { maxWidth: 1220, margin: "0 auto", display: "grid", gap: 16 };
const hero = { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(330px,.8fr)", gap: 18, alignItems: "center", padding: 24, borderRadius: 8, background: "linear-gradient(135deg,#06121f,#12372f 55%,#272048)", color: "#fff", overflow: "hidden" };
const heroCompact = { ...hero, gridTemplateColumns: "1fr", padding: 16 };
const heroCopy = { display: "grid", gap: 12 };
const eyebrow = { fontSize: 12, textTransform: "uppercase", letterSpacing: 0, color: "#a7f3d0", fontWeight: 900 };
const title = { margin: 0, fontSize: 38, lineHeight: 1.04, maxWidth: 740 };
const subtitle = { maxWidth: 760, margin: 0, color: "rgba(255,255,255,.78)", fontSize: 15 };
const heroActions = { display: "flex", flexWrap: "wrap", gap: 10 };
const heroVisual = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const heroVisualCompact = { ...heroVisual, gridTemplateColumns: "repeat(auto-fit,minmax(135px,1fr))" };
const floatingProduct = { display: "grid", gap: 8, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.18)", borderRadius: 8, padding: 10, backdropFilter: "blur(8px)" };
const productArt = { width: "100%", height: 108, borderRadius: 8, objectFit: "cover", display: "block" };
const productArtSmall = { width: "100%", height: 118, borderRadius: 8, objectFit: "cover", display: "block" };
const sparkle = { position: "absolute", right: 10, top: 6, color: "#fff", fontSize: 28, fontWeight: 900 };
const tinyText = { color: "rgba(255,255,255,.72)", fontSize: 12 };
const badge = { borderRadius: 8, padding: 14, background: "#dcfce7", color: "#064e3b", fontWeight: 900 };
const lockedBadge = { ...badge, background: "#fef3c7", color: "#78350f" };
const limitGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 };
const metric = { borderRadius: 8, border: "1px solid rgba(0,0,0,.1)", background: "#fff", padding: 14 };
const metricValue = { fontWeight: 900, fontSize: 20 };
const grid = { display: "grid", gridTemplateColumns: "minmax(0,1.18fr) minmax(330px,.82fr)", gap: 16 };
const gridCompact = { ...grid, gridTemplateColumns: "1fr" };
const panel = { borderRadius: 8, border: "1px solid rgba(0,0,0,.1)", background: "#fff", padding: 16 };
const sectionHeader = { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start", marginBottom: 12 };
const sectionTitle = { fontSize: 19, fontWeight: 900, marginBottom: 6 };
const scannerBadge = { borderRadius: 999, background: "#eef2ff", color: "#3730a3", padding: "6px 10px", fontWeight: 900, whiteSpace: "nowrap" };
const scannerControl = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, padding: 12, marginBottom: 12, background: "#f8fafc" };
const scanNumber = { fontWeight: 900, marginTop: 8 };
const bucketGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginBottom: 14 };
const bucketButton = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f7faf9", padding: "10px 12px", textAlign: "left", fontWeight: 800, cursor: "pointer" };
const bucketButtonActive = { ...bucketButton, borderColor: "#0f766e", background: "#ecfdf5", boxShadow: "0 0 0 2px rgba(15,118,110,.14)" };
const productGridWide = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 };
const productCard = { display: "grid", gap: 6, textAlign: "left", border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 12 };
const productCardActive = { ...productCard, borderColor: "#0f766e", boxShadow: "0 0 0 2px rgba(15,118,110,.16)" };
const statusPill = { width: "fit-content", borderRadius: 999, background: "#eef6ff", padding: "3px 8px", fontSize: 12, fontWeight: 800 };
const muted = { color: "#667085", fontSize: 13 };
const label = { display: "block", marginTop: 12, marginBottom: 6, fontSize: 13, fontWeight: 800 };
const input = { width: "100%", border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, padding: "10px 12px", fontSize: 14, background: "#fff" };
const textarea = { ...input, minHeight: 220, resize: "vertical", fontFamily: "inherit" };
const textareaCompact = { ...input, minHeight: 120, resize: "vertical", fontFamily: "inherit", marginTop: 10 };
const actions = { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 };
const button = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "10px 12px", fontWeight: 900, cursor: "pointer" };
const brightButton = { ...button, background: "#22c55e", color: "#052e16" };
const blackButton = { ...button, textDecoration: "none", display: "inline-flex", alignItems: "center" };
const lightButton = { border: "1px solid rgba(255,255,255,.32)", borderRadius: 8, background: "rgba(255,255,255,.12)", color: "#fff", padding: "10px 12px", fontWeight: 900, textDecoration: "none" };
const outlineButton = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", color: "#111827", padding: "10px 12px", fontWeight: 900, textDecoration: "none" };
const successButton = { ...outlineButton, background: "#dcfce7", borderColor: "#86efac", color: "#14532d" };
const errorBox = { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 8, padding: 12 };
const upgradeBand = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, border: "1px solid #dbeafe", background: "#eff6ff", borderRadius: 8, padding: 14 };
const connectorGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 10 };
const connectorCard = { display: "grid", gap: 10, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fbfcff", padding: 12 };
const connectorTop = { display: "grid", gridTemplateColumns: "38px 1fr auto", gap: 10, alignItems: "center" };
const connectorMark = { width: 38, height: 38, borderRadius: 8, display: "grid", placeItems: "center", color: "#fff", fontWeight: 900 };
const connectorSummary = { color: "#344054", fontSize: 13, lineHeight: 1.4 };
const themeGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 };
const themeCard = { display: "grid", gap: 8, textAlign: "left", border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 12 };
const themeCardActive = { ...themeCard, borderColor: "#2563eb", boxShadow: "0 0 0 2px rgba(37,99,235,.16)" };
const themePreview = { border: "1px solid rgba(0,0,0,.08)", borderRadius: 8, overflow: "hidden", background: "#f8fafc" };
const themeHeroBar = { height: 16 };
const themeImage = { width: "100%", height: 92, objectFit: "cover", display: "block" };
const themeLines = { display: "grid", gap: 5, padding: 8 };
const themeSections = { color: "#475467", fontSize: 12, lineHeight: 1.35 };
const masterGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 10 };
const masterCard = { display: "grid", gridTemplateColumns: "34px 1fr", gap: 10, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, padding: 12, background: "#f8fafc", textAlign: "left", cursor: "pointer" };
const masterCardActive = { ...masterCard, background: "#ecfdf5", borderColor: "#10b981", boxShadow: "0 0 0 2px rgba(16,185,129,.16)" };
const toolIcon = { width: 32, height: 32, borderRadius: 8, display: "grid", placeItems: "center", background: "#111827", color: "#fff", fontWeight: 900 };
const toolConsole = { border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 8, padding: 14, marginTop: 12 };
const scanActions = { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 14 };
const contextBox = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f8fafc", padding: 12, marginTop: 12 };
const resultNotice = { border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e3a8a", borderRadius: 8, padding: 12, marginTop: 12, fontSize: 13 };
const hintBox = { border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: 8, padding: 10, fontSize: 13, marginTop: 8 };
const keywordWrap = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 };
const resultGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10, marginTop: 14 };
const resultCard = { display: "grid", gap: 8, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 12 };
const resultTop = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start" };
const scorePill = { borderRadius: 999, background: "#dcfce7", color: "#166534", padding: "4px 8px", fontSize: 12, fontWeight: 900 };
const resultMetrics = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, color: "#344054", fontSize: 12 };
const resultFooter = { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" };
const resultActions = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 };
const miniButton = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "7px 9px", fontSize: 12, fontWeight: 900, cursor: "pointer" };
const miniLink = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", color: "#111827", padding: "7px 9px", fontSize: 12, fontWeight: 900, textDecoration: "none" };
const growthHeaderActions = { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" };
const growthMetricGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 10 };
const growthDetailGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 8, marginBottom: 14 };
const tinyInsight = { display: "grid", gap: 3, border: "1px solid rgba(0,0,0,.08)", borderRadius: 8, background: "#fbfcff", padding: 10, color: "#475467", fontSize: 12 };
const growthSplit = { display: "grid", gridTemplateColumns: "minmax(0,1.25fr) minmax(300px,.75fr)", gap: 12, marginTop: 12 };
const growthSplitCompact = { ...growthSplit, gridTemplateColumns: "1fr" };
const growthPanel = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 12, minWidth: 0 };
const tableWrap = { overflowX: "auto", border: "1px solid rgba(0,0,0,.08)", borderRadius: 8 };
const dataTable = { width: "100%", borderCollapse: "collapse", minWidth: 760, fontSize: 13 };
const tableHead = { textAlign: "left", padding: "10px 8px", background: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,.08)", color: "#475467", fontSize: 12 };
const tableCell = { padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,.06)", verticalAlign: "top" };
const videoSignalList = { display: "grid", gap: 8 };
const videoSignalBase = { display: "grid", gap: 4, textAlign: "left", borderRadius: 8, padding: 10, cursor: "pointer" };
const videoSignalWinning = { ...videoSignalBase, border: "1px solid #86efac", background: "#f0fdf4" };
const videoSignalWeak = { ...videoSignalBase, border: "1px solid #fed7aa", background: "#fff7ed" };
const recommendationGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 8 };
const recommendationCard = { display: "grid", gap: 7, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f8fafc", padding: 10 };
const recFooter = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 };
const managedPanel = { ...growthPanel, background: "#f8fbff", borderColor: "#bfdbfe" };
const managedCopy = { margin: "0 0 10px", color: "#344054", fontSize: 13, lineHeight: 1.45 };
const feeGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 };
const feeCard = { display: "grid", gap: 5, borderRadius: 8, border: "1px solid #dbeafe", background: "#fff", padding: 10, color: "#344054", fontSize: 13 };
const activityLog = { display: "grid", gap: 6, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#111827", color: "#fff", padding: 12, marginTop: 12, fontSize: 13 };
const themeLinesChild = "unused";

void themeLinesChild;
